/**
 * Error Logging Service
 * 
 * Comprehensive error logging system that captures ALL errors users encounter.
 * This allows admins to see errors before users report them.
 * 
 * Features:
 * - Automatic error capture (React, JS, Promise rejections)
 * - Full context (user, page, browser, device)
 * - Automatic deduplication (same error = increment count)
 * - Non-blocking (doesn't slow down app)
 * - Privacy-safe (doesn't log sensitive data)
 */

import { supabase } from '../lib/supabaseClient';

export type ErrorType = 
  | 'react_error'
  | 'js_error'
  | 'api_error'
  | 'network_error'
  | 'unhandled_promise'
  | 'service_worker_error';

export interface ErrorLogData {
  error_type: ErrorType;
  error_message: string;
  error_stack?: string;
  error_name?: string;
  page_path?: string;
  page_name?: string;
  component_name?: string;
  component_stack?: string;
  metadata?: Record<string, any>;
  url?: string;
  line_number?: number;
  column_number?: number;
  source_file?: string;
  user_id?: string;
  business_id?: string;
}

/**
 * Get device type from user agent
 */
function getDeviceType(): string {
  if (typeof window === 'undefined') return 'unknown';
  
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Parse browser info from user agent
 */
function getBrowserInfo(): { name: string; version: string; os: string; osVersion: string } {
  if (typeof window === 'undefined') {
    return { name: 'unknown', version: 'unknown', os: 'unknown', osVersion: 'unknown' };
  }
  
  const ua = navigator.userAgent;
  let browserName = 'unknown';
  let browserVersion = 'unknown';
  let osName = 'unknown';
  let osVersion = 'unknown';
  
  // Browser detection
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browserName = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browserName = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Firefox')) {
    browserName = 'Firefox';
    const match = ua.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Edg')) {
    browserName = 'Edge';
    const match = ua.match(/Edg\/(\d+)/);
    browserVersion = match ? match[1] : 'unknown';
  }
  
  // OS detection
  if (ua.includes('Windows')) {
    osName = 'Windows';
    const match = ua.match(/Windows NT (\d+\.\d+)/);
    osVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('Mac OS X') || ua.includes('Macintosh')) {
    osName = 'macOS';
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : 'unknown';
  } else if (ua.includes('Linux')) {
    osName = 'Linux';
  } else if (ua.includes('Android')) {
    osName = 'Android';
    const match = ua.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : 'unknown';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    osName = 'iOS';
    const match = ua.match(/OS (\d+[._]\d+)/);
    osVersion = match ? match[1].replace('_', '.') : 'unknown';
  }
  
  return { name: browserName, version: browserVersion, os: osName, osVersion };
}

/**
 * Get or create session ID
 */
function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  
  let sessionId = sessionStorage.getItem('error_log_session_id');
  if (!sessionId) {
    sessionId = `error_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('error_log_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Sanitize error data to remove sensitive information
 */
function sanitizeErrorData(data: ErrorLogData): ErrorLogData {
  const sanitized = { ...data };
  
  // Remove sensitive data from metadata
  if (sanitized.metadata) {
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'auth', 'authorization', 'cookie'];
    const sanitizedMetadata = { ...sanitized.metadata };
    
    for (const key in sanitizedMetadata) {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
        sanitizedMetadata[key] = '[REDACTED]';
      }
    }
    
    sanitized.metadata = sanitizedMetadata;
  }
  
  // Truncate very long error messages/stacks
  if (sanitized.error_message && sanitized.error_message.length > 5000) {
    sanitized.error_message = sanitized.error_message.substring(0, 5000) + '...[truncated]';
  }
  
  if (sanitized.error_stack && sanitized.error_stack.length > 10000) {
    sanitized.error_stack = sanitized.error_stack.substring(0, 10000) + '...[truncated]';
  }
  
  return sanitized;
}

/**
 * Log an error to the database
 * Non-blocking - errors in logging won't break the app
 */
export async function logError(data: ErrorLogData): Promise<void> {
  try {
    // Check for active session before attempting to log
    // Without a session, the RLS policy will reject the insert anyway
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return; // No session = no point trying to write to DB
    }
    
    // Get current user and business
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    // Get business ID from localStorage or context
    let businessId: string | undefined;
    try {
      const selectedBusiness = localStorage.getItem('selectedBusiness');
      if (selectedBusiness) {
        const business = JSON.parse(selectedBusiness);
        businessId = business.id;
      }
    } catch {
      // Ignore parse errors
    }
    
    // Get browser and device info
    const browserInfo = getBrowserInfo();
    const deviceType = getDeviceType();
    const sessionId = getSessionId();
    
    // Get current page info
    const pagePath = window.location.pathname;
    const url = window.location.href;
    
    // Sanitize error data
    const sanitized = sanitizeErrorData(data);
    
    // Prepare error log entry
    const errorLog = {
      user_id: userId || null,
      business_id: businessId || null,
      error_type: sanitized.error_type,
      error_message: sanitized.error_message,
      error_stack: sanitized.error_stack || null,
      error_name: sanitized.error_name || null,
      page_path: sanitized.page_path || pagePath,
      page_name: sanitized.page_name || null,
      component_name: sanitized.component_name || null,
      component_stack: sanitized.component_stack || null,
      user_agent: navigator.userAgent,
      device_type: deviceType,
      browser_name: browserInfo.name,
      browser_version: browserInfo.version,
      os_name: browserInfo.os,
      os_version: browserInfo.osVersion,
      metadata: sanitized.metadata || null,
      url: sanitized.url || url,
      line_number: sanitized.line_number || null,
      column_number: sanitized.column_number || null,
      source_file: sanitized.source_file || null,
      session_id: sessionId,
      ip_address: null, // Would need edge function to get real IP
    };
    
    // Insert error log (non-blocking)
    // The database trigger will handle deduplication
    const { error } = await supabase
      .from('error_logs')
      .insert(errorLog);
    
    if (error) {
      // Don't throw - just log to console
      // We don't want error logging to break the app
      console.error('Failed to log error to database:', error);
    } else {
      console.log('✅ Error logged to database:', sanitized.error_type, sanitized.error_message.substring(0, 100));
    }
  } catch (error) {
    // Silently fail - error logging should never break the app
    console.error('Error logging failed (non-critical):', error);
  }
}

/**
 * Log a React component error
 */
export async function logReactError(
  error: Error,
  errorInfo: ErrorInfo,
  componentName?: string
): Promise<void> {
  await logError({
    error_type: 'react_error',
    error_message: error.message,
    error_stack: error.stack,
    error_name: error.name,
    component_name: componentName,
    component_stack: errorInfo.componentStack,
    page_path: window.location.pathname,
    metadata: {
      errorInfo: {
        componentStack: errorInfo.componentStack,
      }
    }
  });
}

/**
 * Log a JavaScript error
 */
export async function logJSError(
  message: string,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): Promise<void> {
  await logError({
    error_type: 'js_error',
    error_message: message,
    error_stack: error?.stack,
    error_name: error?.name,
    source_file: source,
    line_number: lineno,
    column_number: colno,
    metadata: {
      originalError: error ? {
        name: error.name,
        message: error.message,
      } : null
    }
  });
}

/**
 * Log an unhandled promise rejection
 */
export async function logPromiseRejection(
  reason: any,
  promise?: Promise<any>
): Promise<void> {
  const errorMessage = reason?.message || String(reason);
  const errorStack = reason?.stack;
  const errorName = reason?.name || 'UnhandledPromiseRejection';
  
  await logError({
    error_type: 'unhandled_promise',
    error_message: errorMessage,
    error_stack: errorStack,
    error_name: errorName,
    metadata: {
      reason: typeof reason === 'object' ? JSON.stringify(reason) : reason,
    }
  });
}

/**
 * Log an API error (optional - can be noisy)
 */
export async function logAPIError(
  url: string,
  method: string,
  status: number,
  error: any,
  requestData?: any
): Promise<void> {
  // Only log 5xx errors (server errors) to avoid noise from client errors
  if (status < 500) {
    return;
  }
  
  await logError({
    error_type: 'api_error',
    error_message: `API Error: ${method} ${url} - ${status}`,
    error_stack: error?.stack,
    error_name: `HTTP${status}`,
    metadata: {
      url,
      method,
      status,
      requestData: sanitizeErrorData({ metadata: requestData }).metadata,
    }
  });
}

/**
 * Initialize global error handlers
 * Call this once in main.tsx or App.tsx
 */
export function initializeErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  
  // Handle unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    logJSError(
      event.message,
      event.filename,
      event.lineno,
      event.colno,
      event.error
    ).catch(() => {
      // Silently fail
    });
  });
  
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logPromiseRejection(event.reason).catch(() => {
      // Silently fail
    });
  });
  
  // Listen for service worker errors
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SERVICE_WORKER_ERROR') {
        const error = event.data.error;
        logJSError(
          error.message || 'Service Worker Error',
          error.filename,
          error.lineno,
          error.colno,
          error.stack ? new Error(error.message) : undefined
        ).catch(() => {});
      } else if (event.data?.type === 'SERVICE_WORKER_UNHANDLED_REJECTION') {
        const error = event.data.error;
        logPromiseRejection(error.reason || error).catch(() => {});
      }
    });
  }
  
  console.log('✅ Global error handlers initialized');
}
